"use client";
import React, { useState, useEffect, useRef } from "react";
import styles from "./learning-path-tabs.module.scss";
import ResourceLinks from "../right-rail/resource-links/resource-links";
import { FaqsSection } from "../modal/faqs";
import sanitize from "sanitize-html";
import Feedback from "../center-content/feedback/feedback";
import CommonCourseDetailSection from "./common-course-details-section";
import useAuth from "@/utils/hooks/login-auth";
import useResponsive from "@/utils/hooks/Responsive-Screens";
import ContentContainer from "../center-content";
import ImageModal from "../modal/image-modal";

// Simple transcript display component
const TranscriptDisplay = ({ transcriptData }) => {
  if (!transcriptData) {
    return <p>Transcript will be available soon for this lesson.</p>;
  }

  return (
    <>
      {/* Visible transcript in tab */}
      <div style={{ height: '500px', overflowY: 'auto', lineHeight: 'inherit' }}>
        <div style={{ whiteSpace: 'pre-wrap' }}>
          {transcriptData}
        </div>
      </div>
      
      {/* Hidden transcript for AI/SEO crawling - positioned off-screen but accessible to crawlers */}
      <div 
        style={{ 
          position: 'absolute', 
          left: '-9999px', 
          top: '-9999px',
          width: '1px',
          height: '1px',
          overflow: 'hidden'
        }}
        aria-hidden="true"
      >
        <h3>Video Transcript</h3>
        <div>{transcriptData}</div>
      </div>
    </>
  );
};

const TabsDropdown = ({
  activeTab,
  setActiveTab,
  hasOverView,
  loggedInUser,
  hasResources,
  hasFaqs,
  hasTranscript,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const options = [
    hasOverView && { label: "Overview", value: "overview" },
    loggedInUser && {
      label: "Comments & Feedback",
      value: "comments-and-feedback",
    },
    hasResources && { label: "Resources", value: "resources" },
    hasFaqs && { label: "FAQs", value: "feedback" },
    hasTranscript && { label: "Transcript", value: "transcript" },
  ].filter(Boolean);

  const [focusedIndex, setFocusedIndex] = useState(
    options.findIndex((opt) => opt.value === activeTab)
  );
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (val) => {
    setActiveTab(val);
    setIsOpen(false);
  };
  const handleKeyDown = (e) => {
    if (!isOpen) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setFocusedIndex((prev) => (prev + 1) % options.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setFocusedIndex((prev) => (prev - 1 + options.length) % options.length);
    } else if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleSelect(options[focusedIndex].value);
    } else if (e.key === "Escape") {
      setIsOpen(false);
    }
  };
  return (
    <div
      ref={dropdownRef}
      className={styles["custom-dropdown"]}
    >
      <button
        className={`${styles["custom-dropdown__toggle"]} ada-focus`}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls="tabs-dropdown-list"
        aria-label="Select tab"
        onClick={() => setIsOpen((prev) => !prev)}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
            setIsOpen(true);
          }
        }}
        type="button"
      >
        {options.find((opt) => opt.value === activeTab)?.label || "Select"}
        <span className={styles["custom-dropdown__arrow"]}>▾</span>
      </button>

      {isOpen && (
        <ul
          className={styles["custom-dropdown__menu"]}
          id="tabs-dropdown-list"
          role="listbox"
          aria-label="Tabs"
          tabIndex={-1}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              handleSelect(options[focusedIndex].value);
            }
          }}
        >
          {options?.map((opt, i) => (
            <li
              key={i}
              className={`${styles["custom-dropdown__item"]} ada-focus`}
              role="option"
              aria-selected={activeTab === opt.value}
              tabIndex={activeTab === opt.value ? 0 : -1}
              onClick={() => handleSelect(opt.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  handleSelect(opt.value);
                }
              }}
              aria-label={opt.label}
            >
              {opt.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const DetailsTab = ({
  data,
  term,
  startCourse,
  videoEntry,
  content,
  isStandaloneCourse,
  pageData,
  isResponsive,
  isTextLesson,
  description,
  resources,
  faqs,
  handleFeedbackSubmit,
  isLesson,
  close,
  type = "course",
  transcriptData,
}) => {
  const { loggedInUser } = useAuth();
  const hasOverView =
    data?.overview ||
    videoEntry?.text_lesson ||
    description ||
    data?.description ||
    data?.learning_pathway_details?.description ||
    content;
  const [activeTab, setActiveTab] = useState(
    hasOverView ? "overview" : "comments-and-feedback"
  );
  const { isMobile } = useResponsive();

  // Check if resources, faqs, and transcript are available
  const hasResources =
    resources && Array.isArray(resources) && resources.length > 0;
  const hasFaqs = faqs && Array.isArray(faqs) && faqs.length > 0;
  const hasTranscript = isLesson && transcriptData && transcriptData.trim();
  // Set active tab to overview if the current tab is disabled
  useEffect(() => {
    if (
      (activeTab === "resources" && !hasResources) ||
      (activeTab === "feedback" && !hasFaqs) ||
      (activeTab === "transcript" && !hasTranscript)
    ) {
      setActiveTab("overview");
    }
  }, [activeTab, hasResources, hasFaqs, hasTranscript]);

  return (
    (hasOverView || loggedInUser) && (
      <div className="d-flex flex-column justify-content-center cs-gap-24 align-items-start mw-1920 w-100 pb-4">
        {isMobile ? (
          <TabsDropdown
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            hasOverView={hasOverView}
            loggedInUser={loggedInUser}
            hasResources={hasResources}
            hasFaqs={hasFaqs}
            hasTranscript={hasTranscript}
          />
        ) : (
          <div
            className={styles["tabs-container"]}
            role="tablist"
            aria-label="Course tabs"
          >
            {hasOverView && (
              <div
                className={`outline-focussed ${styles["tabs-item"]} ${activeTab === "overview" ? styles["active"] : ""} ${styles["has-data"]}`}
                data-label={"Overview"}
                role="tab"
                aria-label="Overview"
                id="tab-overview"
                onClick={() => setActiveTab("overview")}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ")
                    setActiveTab("overview");
                }}
                tabIndex={activeTab === "overview" ? 0 : -1}
              >
                Overview
              </div>
            )}
            {loggedInUser && (
              <div
                className={`outline-focussed ${styles["tabs-item"]} ${activeTab === "comments-and-feedback" ? styles["active"] : ""} ${styles["has-data"]}`}
                role="tab"
                aria-selected={activeTab === "comments-and-feedback"}
                aria-controls="tabpanel-comments"
                id="tab-comments"
                tabIndex={0}
                onClick={() => setActiveTab("comments-and-feedback")}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ")
                    setActiveTab("comments-and-feedback");
                }}
                aria-label="Comments and feedback"
              >
                Comments and feedback
              </div>
            )}
            {hasResources && (
              <div
                className={`outline-focussed ${styles["tabs-item"]} ${activeTab === "resources" ? styles["active"] : ""} ${styles["has-data"]}`}
                role="tab"
                aria-selected={activeTab === "resources"}
                aria-controls="tabpanel-resources"
                id="tab-resources"
                tabIndex={0}
                onClick={() => setActiveTab("resources")}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ")
                    setActiveTab("resources");
                }}
                aria-label="Resources"
              >
                Resources
              </div>
            )}
            {hasFaqs && (
              <div
                className={`outline-focussed ${styles["tabs-item"]} ${activeTab === "feedback" ? styles["active"] : ""} ${styles["has-data"]}`}
                role="tab"
                aria-selected={activeTab === "feedback"}
                aria-controls="tabpanel-faq"
                id="tab-faq"
                tabIndex={0}
                onClick={() => setActiveTab("feedback")}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ")
                    setActiveTab("feedback");
                }}
                aria-label="FAQ"
              >
                FAQ
              </div>
            )}
            {hasTranscript && (
              <div
                className={`outline-focussed ${styles["tabs-item"]} ${activeTab === "transcript" ? styles["active"] : ""} ${styles["has-data"]}`}
                role="tab"
                aria-selected={activeTab === "transcript"}
                aria-controls="tabpanel-transcript"
                id="tab-transcript"
                tabIndex={0}
                onClick={() => setActiveTab("transcript")}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ")
                    setActiveTab("transcript");
                }}
                aria-label="Transcript"
              >
                Transcript
              </div>
            )}
          </div>
        )}
        <div
          className={styles["tabs-content"]}
          style={{
            paddingTop: "0px",
            paddingBottom: isLesson && "40px",
            borderBottom: isLesson && "1px solid #DDE3EE",
          }}
        >
          {activeTab === "overview" && (
            <div
              className={styles["tabs-content-panel"]}
              role="tabpanel"
              id="tabpanel-overview"
              aria-labelledby="tab-overview"
              tabIndex={0}
            >
              {!isTextLesson &&
                (videoEntry?.text_lesson ? (
                  <div className="d-flex flex-column cs-gap-40">
                    <ContentContainer content={videoEntry?.text_lesson} />{" "}
                  </div>
                ) : (
                  <div className="d-flex flex-column cs-gap-40">
                    {" "}
                    <ContentContainer
                      content={
                        data?.description ||
                        pageData?.learning_pathway_details?.description
                      }
                    />
                    <ContentContainer content={data?.overview} />
                  </div>
                ))}

              {isTextLesson && (
                <>
                  {/* center content  */}

                  <div className="row">
                    <ContentContainer content={content} />
                  </div>
                </>
              )}
              {!isLesson && !isStandaloneCourse && (
                <div className="mw-1920">
                  <CommonCourseDetailSection
                    pageData={pageData}
                    type={type}
                    key="lp-course-tab"
                  />
                </div>
              )}
            </div>
          )}
          {activeTab === "comments-and-feedback" && loggedInUser && (
            <div
              className={styles["tabs-content-panel"]}
              role="tabpanel"
              id="tabpanel-comments"
              aria-labelledby="tab-comments"
              tabIndex={0}
            >
              <Feedback
                data={data}
                handleSubmit={handleFeedbackSubmit}
              />
            </div>
          )}
          {activeTab === "resources" && hasResources && (
            <div
              className={styles["tabs-content-panel"]}
              role="tabpanel"
              id="tabpanel-resources"
              aria-labelledby="tab-resources"
              tabIndex={0}
            >
              <div className="course-resources py-0">
                {/* <h3 className="body-5 mb-0">Course resources</h3> */}
                <ResourceLinks
                  ctaData={resources}
                  // faqs={faqs}
                />
              </div>
            </div>
          )}
          {activeTab === "feedback" && hasFaqs && (
            <div
              className={styles["tabs-content-panel"]}
              role="tabpanel"
              id="tabpanel-faq"
              aria-labelledby="tab-faq"
              tabIndex={0}
            >
              <FaqsSection faqs={faqs} />
            </div>
          )}
          {activeTab === "transcript" && hasTranscript && (
            <div
              className={styles["tabs-content-panel"]}
              role="tabpanel"
              id="tabpanel-transcript"
              aria-labelledby="tab-transcript"
              tabIndex={0}
            >
              <TranscriptDisplay transcriptData={transcriptData} />
            </div>
          )}
        </div>
      </div>
    )
  );
};

export default DetailsTab;
